import { ReactNode } from 'react'

export type SortDirection = 'asc' | 'desc'

export type TableSort = { key: string; direction: SortDirection }

export type TableColumn<Row> = {
  key: string
  label: string
  sortable?: boolean
  render: (row: Row) => ReactNode
  cellClassName?: string
}

function _getHeaderClass(column: TableColumn<unknown>, sort: TableSort | undefined): string {
  if (!column.sortable) {
    return 'sorting_disabled'
  }
  return sort?.key === column.key ? `sorting_${sort.direction}` : 'sorting'
}

function _getAriaSort(column: TableColumn<unknown>, sort: TableSort | undefined): 'ascending' | 'descending' | undefined {
  if (!column.sortable || sort?.key !== column.key) {
    return undefined
  }
  return sort.direction === 'asc' ? 'ascending' : 'descending'
}

export function Table<Row>({
  columns,
  rows,
  getRowKey,
  sort,
  onSort,
  emptyMessage,
  loading = false,
  loadingMessage = 'Cargando...',
  className = '',
  style,
}: {
  columns: readonly TableColumn<Row>[]
  rows: readonly Row[]
  getRowKey: (row: Row) => string | number
  sort?: TableSort
  onSort?: (key: string) => void
  emptyMessage: ReactNode
  loading?: boolean
  loadingMessage?: string
  className?: string
  style?: React.CSSProperties
}) {
  const sortColumn = (column: TableColumn<Row>) => {
    if (column.sortable && onSort) {
      onSort(column.key)
    }
  }

  return (
    <div className="table-responsive border">
      <table className={`table table-striped dataTable no-footer ${className}`.trim()} style={style}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={_getHeaderClass(column as TableColumn<unknown>, sort)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                tabIndex={column.sortable ? 0 : undefined}
                aria-label={
                  column.sortable ? `${column.label}: Activar para ordenar la columna de manera ascendente` : column.label
                }
                aria-sort={_getAriaSort(column as TableColumn<unknown>, sort)}
                onClick={() => sortColumn(column)}
                onKeyDown={(event) => {
                  if (column.sortable && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    sortColumn(column)
                  }
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length}>{loadingMessage}</td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td className="default-text-color" colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={getRowKey(row)} data-testid={`row-${getRowKey(row)}`}>
              {columns.map((column) => (
                <td key={column.key} className={column.cellClassName}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
